import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useStorage } from '@vueuse/core'
import { useFlowState } from '../composables/flowState'
import { useSentry } from '../composables/sentry'
import SurchargeService from '../services/SurchargeService'
import { type Surcharge, toSurchargeApplicable } from '../services/SurchargeServiceInterface'

export const useSurchargesStore = defineStore('SurchargesStore', () => {
  // General
  const { flow, FlowState } = useFlowState()
  const { handleError } = useSentry()
  const toast = useToast()

  const editing = ref(false)
  const surcharges = ref<Surcharge[]>([])
  const configuratorsWithSurcharges = ref(new Map())

  function buildConfiguratorsWithSurcharges(surcharges: Surcharge[]) {
    configuratorsWithSurcharges.value.clear()
    surcharges.forEach((surcharge) => {
      if (!surcharge.active || !surcharge?.configurators?.length || surcharge.apply_to !== 'configurators')
        return

      surcharge.configurators.forEach((configurator) => {
        if (!configuratorsWithSurcharges.value.has(configurator.id)) {
          configuratorsWithSurcharges.value.set(configurator.id, [surcharge])
        }
        else {
          const existingSurcharges = configuratorsWithSurcharges.value.get(configurator.id) || []
          existingSurcharges.push(surcharge)
          configuratorsWithSurcharges.value.set(configurator.id, existingSurcharges)
        }
      })
    })
  }

  async function getAll(payload?: { forceRefresh?: boolean }) {
    try {
      flow.value = FlowState.Loading
      surcharges.value = []

      const localKey = 'dasco-surcharges'
      const localSurchargesValue = localStorage.getItem(localKey) || ''

      if (!localSurchargesValue || payload?.forceRefresh) {
        if (payload?.forceRefresh) {
          localStorage.removeItem(localKey)
        }

        const res = await SurchargeService.getAll()
        if (!res.success)
          throw new Error(res.msg || 'Failed to fetch surcharges')

        if (res.data) {
          useStorage(localKey, { lastFetched: Date.now(), data: res.data }, localStorage)
          surcharges.value = res.data?.length ? res.data : []
        }
      }
      else {
        const localData = JSON.parse(localSurchargesValue).data || []
        surcharges.value = localData
      }

      buildConfiguratorsWithSurcharges(surcharges.value)
    }
    catch (error: any) {
      handleError(error)
      toast.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 })
    }
    finally {
      flow.value = FlowState.Idle
    }
  }

  function getApplicableSurcharges(payload: any) {
    const {
      id,
      getQuoteLine = false,
    } = payload
    const applicableSurcharges: any = []

    // If getQuoteLine is true
    if (getQuoteLine) {
      surcharges.value.forEach((surcharge) => {
        if (surcharge.active && surcharge.apply_to === 'order-line') {
          applicableSurcharges.push(toSurchargeApplicable(surcharge))
        }
      })
    }
    // If this configurator has surcharges, add them
    if (configuratorsWithSurcharges.value.has(id)) {
      const arr = configuratorsWithSurcharges.value.get(id) || []
      arr.forEach((i: Surcharge) => {
        // If the surcharge is active and applies to configurators, add it
        // The quote module will handle applying any surcharges that are applicable for that line
        applicableSurcharges.push(toSurchargeApplicable(i, !getQuoteLine))
      })
    }
    // Add surcharges that are always applicable
    surcharges.value.forEach((surcharge) => {
      if (surcharge.active && surcharge.apply_to === 'all') {
        applicableSurcharges.push(toSurchargeApplicable(surcharge))
      }
    })

    // Sort surcharges by calculation_order (primary) and id (secondary)
    applicableSurcharges.sort((a: { calculation_order: number, id: string }, b: { calculation_order: number, id: any }) => {
      // Compare calculation_order first
      if (a.calculation_order !== b.calculation_order) {
        return a.calculation_order - b.calculation_order
      }

      // If calculation_order is the same, compare by id
      return a.id.localeCompare(b.id)
    })
    return applicableSurcharges
  }

  return {
    configuratorsWithSurcharges,
    editing,
    flow,
    FlowState,
    getAll,
    handleError,
    surcharges,
    toast,
    getApplicableSurcharges,
  }
})
